import { useHero } from "@/hooks/useHero";
import { useHomepageConfig } from "@/hooks/useHomepageConfig";
import { heroContent } from "@/data/heroContent";
import TerritorySection from "@/pages/home/components/TerritorySection";
import { useEffect } from "react";

const heroFallback = heroContent.find((h) => h.page === "datos")!;

const socials = [
  { icon: "ri-twitter-x-fill", label: "X" },
  { icon: "ri-youtube-fill", label: "YouTube" },
  { icon: "ri-instagram-fill", label: "Instagram" },
  { icon: "ri-facebook-fill", label: "Facebook" },
  { icon: "ri-linkedin-fill", label: "LinkedIn" },
];

export default function Datos() {
  const { data: datosHero } = useHero("datos", heroFallback);
  const { data: config } = useHomepageConfig();
  const territorioVisible = config?.datos_territorio_visible ?? true;

  // Update page title from hero seo_title
  useEffect(() => {
    const seoTitle = datosHero?.seo_title ?? heroFallback.seoTitle ?? "La Región | MásMed Tarapacá";
    if (seoTitle) {
      document.title = seoTitle;
    }
  }, [datosHero]);

  return (
    <div>
      {/* Hero Datos */}
      <section className="relative w-full h-[580px] sm:h-[640px] md:h-[720px] lg:h-[760px] flex flex-col overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${datosHero?.background_image ?? heroFallback.backgroundImage})` }}
        />

        {/* Overlays: estilo Miradas */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-700/60 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-600/25 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-l from-orange-500/30 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-black/10" />

        {/* Redes sociales verticales izquierda - estilo Miradas */}
        <div className="hidden md:flex absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 z-20 flex-col gap-3 lg:gap-4">
          {socials.map((s) => (
            <a
              key={s.label}
              href="#"
              aria-label={s.label}
              className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border-2 border-white/80 bg-white/10 hover:bg-white/25 hover:border-white hover:scale-110 flex items-center justify-center text-white text-base lg:text-lg transition-all duration-300 cursor-pointer backdrop-blur-sm"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={s.icon} />
              </div>
            </a>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-between w-full px-4 md:px-8 lg:px-12 pt-24 md:pt-28 pb-10 md:pb-12">
          {/* Título y descripción - centrados */}
          <div className="flex-1 flex flex-col items-center justify-center w-full">
            <div className="text-center max-w-3xl">
              <h1 className="font-utendo text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight drop-shadow-lg"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
                {datosHero?.title ?? heroFallback.title}
              </h1>
              <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl mx-auto mt-4 md:mt-5 drop-shadow"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif" }}>
                {datosHero?.subtitle ?? heroFallback.subtitle}
              </p>
            </div>
          </div>

          {/* Bloque institucional - estilo Miradas */}
          <div className="w-full max-w-4xl mx-auto text-center">
            <p className="text-white text-xs sm:text-sm md:text-base leading-relaxed drop-shadow-md max-w-3xl mx-auto"
            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
              Proyecto financiado por el Gobierno Regional de Tarapacá y su Consejo Regional a través del Fondo Regional para la Productividad y Desarrollo (FRPD).
            </p>

            <div className="mt-3 md:mt-4 flex flex-wrap items-center justify-center gap-3 md:gap-5 lg:gap-8">
              <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>Financia:</span>
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/6576e2ace5ad881c20d8c13d63df08f0.png"
                alt="Gobierno Regional de Tarapacá"
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain drop-shadow-md"
                loading="lazy"
              />
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/cea5546c3b2b72c7ff9e242a7eae203e.png"
                alt="CORE Tarapacá"
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md"
                loading="lazy"
              />
              <span className="text-white text-xs md:text-sm uppercase tracking-wider drop-shadow-md"
              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>Ejecuta:</span>
              <img
                src="https://static.readdy.ai/image/1c905122d7916b759598966417b2314b/200e423cba0b1ea675826f32c4682d50.png"
                alt="UNAP Universidad Arturo Prat"
                className="h-8 sm:h-9 md:h-10 lg:h-12 w-auto object-contain drop-shadow-md"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Territorio */}
      {territorioVisible && <TerritorySection />}
    </div>
  );
}